import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalBody, Form, Label, Spinner } from "reactstrap";
import { Icon, Col, Button, RSelect } from "../../../components/Component";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { categoryTypes } from "../../../constants";
import { addCategory, updateCategory, getCategories } from "../../../api/category";
import {
  addCategorySuccess,
  updateCategorySuccess,
} from "../../../slice/categories";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { debounce } from "lodash";

// Helpers for the localized name field. Legacy rows may still have name as
// a plain string until the BE migration runs.
const readNameEn = (name) => {
  if (!name) return "";
  if (typeof name === "string") return name;
  return name.en ?? "";
};
const readNameAr = (name) => {
  if (!name || typeof name === "string") return "";
  return name.ar ?? "";
};

const UpdateCategoryModal = ({ isOpen, closeModal, category }) => {
  const schema = yup
    .object({
      nameEn: yup.string().trim().required("Name (English) is required"),
      nameAr: yup.string().trim().nullable(),
      type: yup.string().required("Type is required"),
      parent: yup.string().nullable(),
    })
    .required();
  const {
    reset,
    register,
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onSubmit",
    resolver: yupResolver(schema),
  });
  const dispatch = useDispatch();
  const [parentOptions, setParentOptions] = useState([]);
  const selectedType = watch("type");

  const preparePayload = (data) => {
    const ar = (data.nameAr ?? "").trim();
    return {
      name: ar ? { en: data.nameEn.trim(), ar } : { en: data.nameEn.trim() },
      type: data.type,
      parent: data.parent === "" ? null : data.parent,
    };
  };

  const onSubmit = async (data) => {
    const payload = preparePayload(data);
    if (category) {
      await onUpdateCategory(payload);
    } else {
      await onAddCategory(payload);
    }
  };

  useEffect(() => {
    const parentValue =
      category?.parent && typeof category.parent !== "string"
        ? category.parent._id
        : category?.parent ?? "";
    reset({
      nameEn: readNameEn(category?.name),
      nameAr: readNameAr(category?.name),
      type: category?.type,
      parent: parentValue,
    });
  }, [reset, category]);

  const fetchParentOptions = useMemo(
    () =>
      debounce(async (keyword, type) => {
        if (!type) {
          return;
        }
        try {
          const res = await getCategories({
            page: 1,
            limit: 10,
            types: [type],
            keyword: keyword?.trim() || undefined,
          });
          const docs = res?.docs ?? res;
          setParentOptions(Array.isArray(docs) ? docs : []);
        } catch {
          setParentOptions([]);
        }
      }, 400),
    []
  );

  useEffect(() => {
    return () => fetchParentOptions.cancel();
  }, [fetchParentOptions]);

  useEffect(() => {
    setParentOptions([]);
    if (!selectedType) {
      return;
    }
    fetchParentOptions("", selectedType);
  }, [selectedType, fetchParentOptions]);

  const parentSelectOptions = useMemo(
    () =>
      parentOptions
        .filter((parent) => parent._id !== category?._id)
        .map((parent) => ({
          value: parent._id,
          label: readNameEn(parent.name) || "(no name)",
        })),
    [parentOptions, category?._id]
  );

  const handleParentInputChange = (inputValue, actionMeta) => {
    if (actionMeta.action === "input-change" && selectedType) {
      fetchParentOptions(inputValue, selectedType);
    }
    return inputValue;
  };

  const onAddCategory = async (data) => {
    try {
      const res = await addCategory(data);
      dispatch(addCategorySuccess(res));
      toast.success("Add category successfully");
      closeModal();
    } catch (error) {
      toast.error(error?.message);
    }
  };

  const onUpdateCategory = async (data) => {
    try {
      const res = await updateCategory(category._id, data);
      dispatch(updateCategorySuccess(res));
      toast.success("Update category successfully");
      closeModal();
    } catch (error) {
      toast.error(error?.message);
    }
  };
  return (
    <Modal
      isOpen={isOpen}
      toggle={() => closeModal()}
      className="modal-dialog-centered"
      size="lg"
    >
      <ModalBody>
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            closeModal();
          }}
          className="close"
        >
          <Icon name="cross-sm"></Icon>
        </a>
        <div className="p-2">
          <h5 className="title">
            {category != null ? "Update" : "Add"} Category
          </h5>
          <div className="mt-4">
            <Form
              className="row gy-4 form-validate is-alter"
              noValidate
              onSubmit={handleSubmit(onSubmit)}
            >
              <Col md="6">
                <div className="form-group">
                  <label className="form-label">Name (English)</label>
                  <div className="form-control-wrap">
                    <input
                      className="form-control"
                      type="text"
                      {...register("nameEn")}
                      placeholder="Enter name in English"
                    />
                    {errors.nameEn && (
                      <span className="invalid">{errors.nameEn.message}</span>
                    )}
                  </div>
                </div>
              </Col>
              <Col md="6">
                <div className="form-group">
                  <label className="form-label">Name (Arabic)</label>
                  <div className="form-control-wrap">
                    <input
                      className="form-control"
                      type="text"
                      dir="rtl"
                      {...register("nameAr")}
                      placeholder="أدخل الاسم بالعربية"
                    />
                    {errors.nameAr && (
                      <span className="invalid">{errors.nameAr.message}</span>
                    )}
                  </div>
                </div>
              </Col>
              <Col md="12">
                <div className="form-group">
                  <Label className="form-label" htmlFor="fv-role">
                    Type
                  </Label>
                  <div className="form-control-wrap">
                    <div className="form-control-select">
                      <select
                        className="form-control form-select"
                        id="fv-role"
                        {...register("type")}
                        placeholder="Select a option"
                      >
                        {categoryTypes.map((e, i) => (
                          <option key={i} label={e.label} value={e.value}>
                            {e.label}
                          </option>
                        ))}
                      </select>
                      {errors.type && (
                        <span className="invalid">{errors.type.message}</span>
                      )}
                    </div>
                  </div>
                </div>
              </Col>
              <Col md="12">
                <div className="form-group">
                  <Label className="form-label" htmlFor="fv-parent">
                    Parent
                  </Label>
                  <div className="form-control-wrap">
                    <Controller
                      control={control}
                      name="parent"
                      render={({ field }) => {
                        // The first page of `getCategories` may not include
                        // this category's existing parent. Fall back to the
                        // parent object embedded on `category` so the select
                        // shows the current selection immediately on open.
                        let selectedOption =
                          parentSelectOptions.find(
                            (option) => option.value === field.value
                          ) ?? null;
                        if (!selectedOption && field.value) {
                          const embeddedParent =
                            category?.parent &&
                            typeof category.parent === "object"
                              ? category.parent
                              : null;
                          if (
                            embeddedParent &&
                            `${embeddedParent._id}` === `${field.value}`
                          ) {
                            selectedOption = {
                              value: field.value,
                              label:
                                readNameEn(embeddedParent.name) || "(no name)",
                            };
                          }
                        }
                        return (
                          <RSelect
                            inputId="fv-parent"
                            options={parentSelectOptions}
                            value={selectedOption}
                            onChange={(option) =>
                              field.onChange(option ? option.value : "")
                            }
                            onInputChange={handleParentInputChange}
                            placeholder={
                              selectedType
                                ? "Search parent category"
                                : "Select type first"
                            }
                            isDisabled={!selectedType}
                            isClearable
                            noOptionsMessage={() =>
                              selectedType ? "No matching parents" : "Choose type"
                            }
                          />
                        );
                      }}
                    />
                    {errors.parent && (
                      <span className="invalid">{errors.parent.message}</span>
                    )}
                  </div>
                </div>
              </Col>
              <Col size="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button color="primary" size="md" type="submit">
                      {isSubmitting ? (
                        <Spinner size="sm" color="light" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </li>
                  <li>
                    <a
                      href="#cancel"
                      onClick={(ev) => {
                        ev.preventDefault();
                        closeModal();
                      }}
                      className="link link-light"
                    >
                      Cancel
                    </a>
                  </li>
                </ul>
              </Col>
            </Form>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};
export default UpdateCategoryModal;
